import { useEffect, useRef, useState } from 'react';
import { Avatar } from './Avatar.tsx';
import { G, type Glyph } from './icons.tsx';
import { useT } from '../i18n/I18n.tsx';

/**
 * Active conversation header.
 *
 * Carries identity, presence and the actions that belong to *this*
 * conversation. There are deliberately no call buttons: audio and video calls
 * are not part of this release, and a control that does nothing is worse than
 * an absent one.
 */

export interface HeaderAction {
  glyph: Glyph;
  label: string;
  onSelect: () => void;
  danger?: boolean;
}

export function ChatHeader({
  name,
  status,
  onBack,
  onOpenProfile,
  actions,
  menu,
}: {
  name: string;
  status?: React.ReactNode;
  /** Present on narrow screens, where the list and chat are separate views. */
  onBack?: () => void;
  onOpenProfile?: () => void;
  actions?: HeaderAction[];
  menu?: HeaderAction[];
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  return (
    <header className="chat-head">
      {onBack ? (
        <button type="button" className="ibtn" onClick={onBack} aria-label={t('chat.back')}>
          <G n="back" size={24} />
        </button>
      ) : null}

      <button type="button" className="chat-head__id" onClick={onOpenProfile}>
        <Avatar name={name} size="sm" />
        <span className="chat-head__text">
          <span className="chat-head__name">{name}</span>
          {status ? <span className="chat-head__status">{status}</span> : null}
        </span>
      </button>

      <div className="chat-head__actions" ref={box}>
        {actions?.map((action) => (
          <button
            key={action.label}
            type="button"
            className="ibtn"
            onClick={action.onSelect}
            aria-label={action.label}
            title={action.label}
          >
            <G n={action.glyph} size={22} />
          </button>
        ))}

        {menu && menu.length > 0 ? (
          <>
            <button
              type="button"
              className="ibtn"
              onClick={() => setOpen((value) => !value)}
              aria-label={t('common.menu')}
              aria-expanded={open}
              aria-haspopup="menu"
            >
              <G n="menu" size={22} />
            </button>
            {open ? (
              <div className="menu" role="menu">
                {menu.map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    role="menuitem"
                    className={item.danger ? 'menu--danger' : undefined}
                    onClick={() => {
                      setOpen(false);
                      item.onSelect();
                    }}
                  >
                    <G n={item.glyph} size={19} />
                    {item.label}
                  </button>
                ))}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </header>
  );
}
